import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { FormRow, FormSubmissionRow, IssueWhereInput } from "#app/lib/data";
import { BANNER_PRESETS } from "#app/lib/banner-presets";
import { localId } from "#app/lib/local-id";
import { isChoiceCountMet, isDateWindowClosed } from "@salestrak/custom-fields";
import {
  FORM_PROPERTY_FIELD_TYPE,
  FORM_PROPERTY_LABEL,
  FORM_QUESTION_FIELD_TYPE,
  fieldItemCount,
  formSteps,
  INLINE_FIELD_ID_PREFIX,
  choiceCount,
  initialFormOnlyAnswer,
  isFieldItem,
  isFillableItem,
  isPhotoItem,
  linkedDefaultAnswer,
  livePhotoIds,
  propertyField,
  questionKindForFieldType,
  resolveItemField,
  UNTITLED_FORM,
  withItemRules,
  type FormField,
  type FormOffer,
  type FormProperty,
  type FormQuestionKind,
  type FormShare,
  type FormSubmissionStatus,
} from "@salestrak/forms";
import type * as Rules from "@salestrak/forms";

export * from "@salestrak/forms";

export type FormItem = Rules.FormItem<CustomFieldDef>;
export type FormItemKind = FormItem["kind"];
export type FormFieldItem = Rules.FormFieldItem<CustomFieldDef>;
export type FormLinkedItem = Rules.FormLinkedItem<CustomFieldDef>;
export type FormPhotoItem = Rules.FormPhotoItem<CustomFieldDef>;
export type FormShape = Rules.FormShape<CustomFieldDef>;
export type FormStep = Rules.FormStep<CustomFieldDef>;
export type FormWalk = Rules.FormWalk<CustomFieldDef>;

export type Form = Rules.Form<CustomFieldDef> &
  Pick<FormRow, "version" | "bannerPreset" | "bannerMediaId" | "usedCount" | "monthlyCount" | "submittedCount">;

export type FormSubmission = Rules.FormSubmission &
  Pick<FormSubmissionRow, "viewerCanOpen" | "viewerCanDelete" | "viewerCanRemoveShares" | "media"> & {
    items: FormItem[];
        versionNumber: number | null;
    status: FormSubmissionStatus;
  };

export type { FormOffer, FormProperty, FormQuestionKind, FormShare };

function decorateField(field: FormField): CustomFieldDef {
  const config = field.config ?? {};
  return {
    __typename: "CustomField",
    customFieldId: field.customFieldId,
    description: null,
    id: `CustomField:${field.customFieldId}`,
    key: null,
    name: field.name,
    type: field.type as CustomFieldDef["type"],
    config: {
      ...emptyConfig(),
      ...config,
      __typename: "CustomFieldConfigView",
      options: config.options
        ? config.options.map((option) => ({
            __typename: "CustomFieldOptionView" as const,
            color: option?.color ?? null,
            label: option?.label ?? "",
            value: option?.value ?? "",
          }))
        : null,
    } as CustomFieldDef["config"],
  };
}

function decorateItems(items: unknown): FormItem[] {
  return ((Array.isArray(items) ? items : []) as Rules.FormItem[]).map((item) =>
    item.kind === "question" || item.kind === "attachment" ? { ...item, field: decorateField(item.field) } : item,
  ) as FormItem[];
}

function decorateShape(shape: unknown): FormShape | null {
  if (!shape || typeof shape !== "object") return null;
  return { items: decorateItems((shape as { items?: unknown }).items) };
}

export function formFromRow(row: FormRow): Form {
  const settings = row.settings as Rules.FormSettings | null;
  return {
    bannerMediaId: row.bannerMediaId,
    bannerPreset: row.bannerPreset,
    bannerUrl: BANNER_PRESETS.find((preset) => preset.id === row.bannerPreset)?.url ?? null,
    createdAt: row.creationDate,
    createdBy: row.createdBy,
    description: row.description,
    draft: decorateShape(row.draft),
    formId: row.id,
    publishedVersionId: row.publishedVersionId,
    items: decorateItems(row.items),
    monthlyCount: row.monthlyCount,
    name: row.name,
    publishedAt: row.publishedAt,
    publishedBy: row.publishedBy,
    requiredByDefault: row.requiredByDefault,
    sharedWith: row.sharedWith.map((share) => ({ id: share.id, kind: share.kind as FormShare["kind"] })),
    status: row.status as Rules.FormStatus,
    submittedCount: row.submittedCount,
    updatedAt: row.lastUpdateDate,
    usedCount: row.usedCount,
    version: row.version,
    ...(settings ? { settings } : {}),
  };
}

const decodedVersionItems = new WeakMap<object, FormItem[]>();

function versionItems(items: unknown): FormItem[] {
  if (typeof items !== "object" || items === null) return decorateItems(items);
  const known = decodedVersionItems.get(items);
  if (known) return known;
  const decoded = decorateItems(items);
  decodedVersionItems.set(items, decoded);
  return decoded;
}

export function submissionFromRow(row: FormSubmissionRow): FormSubmission {
  return {
    answers: row.answers ?? {},
    createdAt: row.creationDate,
    createdBy: row.createdBy,
    deletedPhotoIds: row.deletedPhotoIds,
    formId: row.formId,
    formVersionId: row.formVersion.id,
    issueId: row.issueId,
    itemIds: row.itemIds,
    items: versionItems(row.formVersion.items),
    media: row.media,
    prefilled: row.prefilled,
    sharedWith: row.sharedWith.map((share) => ({ id: share.id, kind: share.kind as FormShare["kind"] })),
    status: row.status as FormSubmissionStatus,
    submissionId: row.id,
    submittedAt: row.submittedAt,
    submittedBy: row.submittedBy,
    updatedAt: row.lastUpdateDate,
    updatedBy: row.updatedBy,
    viewerCanDelete: row.viewerCanDelete,
    viewerCanOpen: row.viewerCanOpen,
    viewerCanRemoveShares: row.viewerCanRemoveShares,
    versionNumber: row.formVersion.number,
  };
}

export function shapeForSave(shape: FormShape): Rules.FormShape {
  return { items: shape.items };
}

function formOnlyStartingAnswers(items: readonly FormItem[]): Record<string, unknown> {
  const answers: Record<string, unknown> = {};
  for (const item of items) {
    if (item.kind === "question" || item.kind === "attachment") answers[item.itemId] = initialFormOnlyAnswer(item);
  }
  return answers;
}

export function startingAnswers(
  items: readonly FormItem[],
  customFieldsById: ReadonlyMap<string, CustomFieldDef>,
): Record<string, unknown> {
  const answers = formOnlyStartingAnswers(items);
  for (const item of items) {
    if (item.kind === "customField" || item.kind === "property") {
      const defaults = linkedDefaultAnswer(item, resolveItemField(item, customFieldsById));
      if (defaults) answers[item.itemId] = defaults;
    }
  }
  return answers;
}

// Not stored until the first save that holds an answer.
export function newSubmission(
  form: Pick<Form, "formId" | "items" | "publishedVersionId">,
  input: { issueId: string; createdBy: string },
): FormSubmission {
  const at = new Date().toISOString();
  return {
    answers: formOnlyStartingAnswers(form.items),
    createdAt: at,
    createdBy: input.createdBy,
    formId: form.formId,
    formVersionId: form.publishedVersionId ?? null,
    issueId: input.issueId,
    items: form.items,
    media: [],
    sharedWith: [],
    status: "inProgress",
    submissionId: localId(),
    updatedAt: at,
    viewerCanDelete: true,
    viewerCanOpen: true,
    viewerCanRemoveShares: true,
    versionNumber: null,
  };
}

export function submissionActivityAt(submission: Pick<FormSubmission, "updatedAt" | "submittedAt" | "createdAt">): string {
  return submission.updatedAt ?? submission.submittedAt ?? submission.createdAt;
}

export function byNewestActivity(a: FormSubmission, b: FormSubmission): number {
  return submissionActivityAt(b).localeCompare(submissionActivityAt(a));
}

export function submittedOf(submissions: readonly FormSubmission[], formId: string): FormSubmission[] {
  return submissions.filter((row) => row.formId === formId && row.status === "submitted");
}

function withShape(form: Form, shape: FormShape): Form {
  return { ...form, ...shape };
}

export function editableForm(form: Form): Form {
  return form.draft ? withShape(form, form.draft) : form;
}

export type FormPhotosOnFile = {
  form: Form;
  item: FormPhotoItem;
  submission: FormSubmission;
  photoIds: string[];
};

export function formPhotosOnFile(forms: readonly Form[], submissions: readonly FormSubmission[]): FormPhotosOnFile[] {
  const formsById = new Map(forms.map((form) => [form.formId, form]));
  const onFile: FormPhotosOnFile[] = [];
  const newestFirst = submissions
    .filter((row) => row.status === "submitted" && row.viewerCanOpen)
    .sort((a, b) => (b.submittedAt ?? "").localeCompare(a.submittedAt ?? ""));
  for (const submission of newestFirst) {
    const form = formsById.get(submission.formId);
    for (const item of submission.items) {
      if (!form || !isPhotoItem(item)) continue;
      const photoIds = livePhotoIds(submission, item.itemId);
      if (photoIds.length > 0) onFile.push({ form, item, photoIds, submission });
    }
  }
  return onFile;
}

export function committedFormName(next: string, current: string): string | null {
  const name = next || UNTITLED_FORM;
  return name === current ? null : name;
}

export const FORM_QUESTION_LABEL: Record<FormQuestionKind, string> = {
  address: "Location",
  date: "Date",
  email: "Email",
  longText: "Text Area",
  member: "Member",
  money: "Money",
  multipleChoice: "Multi Select",
  number: "Number",
  phone: "Phone",
  shortText: "Text",
  singleChoice: "Single Select",
  time: "Time",
  url: "Website",
  yesNo: "Checkbox",
};

// Timestamp plus random tail so items added in one tick never collide.
function makeItemId(kind: FormItemKind): string {
  return `${kind}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

// Only the item copies; its follow-ups stay with the original, and a form-only field gets its own id.
export function duplicateFormItem(item: FormItem): FormItem {
  const itemId = makeItemId(item.kind);
  if (item.kind === "question" || item.kind === "attachment") {
    const customFieldId = `${INLINE_FIELD_ID_PREFIX}${itemId}`;
    return {
      ...item,
      field: { ...item.field, customFieldId, id: `CustomField:${customFieldId}` },
      itemId,
    };
  }
  return { ...item, itemId };
}

function emptyConfig(): NonNullable<CustomFieldDef["config"]> {
  return {
    __typename: "CustomFieldConfigView",
    allowOptionCreate: null,
    currency: null,
    decimalPlaces: null,
    defaultValue: null,
    includeTeams: null,
    includeTime: null,
    max: null,
    min: null,
    multiline: null,
    multiple: null,
    thousandsSeparator: null,
    optionSort: null,
    options: null,
    showOptionColors: null,
    step: null,
  };
}

function makeInlineField(
  kind: FormQuestionKind | "attachment",
  label: string,
  itemId: string,
): CustomFieldDef {
  const type = kind === "attachment" ? "photos" : FORM_QUESTION_FIELD_TYPE[kind];
  const config = emptyConfig();
  if (kind === "singleChoice" || kind === "multipleChoice") {
    config.options = [];
    config.optionSort = "manual";
  } else if (kind === "attachment") {
    config.multiple = true;
  } else if (kind === "date") {
    config.includeTime = false;
  }
  return {
    __typename: "CustomField",
    config,
    customFieldId: `${INLINE_FIELD_ID_PREFIX}${itemId}`,
    description: null,
    id: `CustomField:${INLINE_FIELD_ID_PREFIX}${itemId}`,
    key: null,
    name: label,
    type,
  };
}

export function makePropertyField(
  property: FormProperty,
  choices: readonly { value: string; label: string }[] = [],
): CustomFieldDef {
  return decorateField(propertyField(property, choices));
}

export function makeFormItem(
  kind: FormQuestionKind | "attachment" | "section" | "text",
): FormItem {
  if (kind === "section") {
    return { itemId: makeItemId("section"), kind: "section", label: "Section" };
  }
  if (kind === "text") {
    return { body: "", itemId: makeItemId("text"), kind: "text", label: "" };
  }
  if (kind === "attachment") {
    const itemId = makeItemId("attachment");
    return {
      field: makeInlineField("attachment", "Photos", itemId),
      itemId,
      kind: "attachment",
      label: "Photos",
      required: false,
    };
  }
  const itemId = makeItemId("question");
  const label = FORM_QUESTION_LABEL[kind];
  return {
    field: makeInlineField(kind, label, itemId),
    itemId,
    kind: "question",
    label,
    required: false,
  };
}

export function makeCustomFieldItem(field: CustomFieldDef): FormItem {
  return {
    customFieldId: field.customFieldId,
    fieldType: field.type,
    itemId: makeItemId("customField"),
    kind: "customField",
    label: field.name,
    required: false,
  };
}

export function makePropertyItem(property: FormProperty): FormItem {
  return {
    itemId: makeItemId("property"),
    kind: "property",
    label: FORM_PROPERTY_LABEL[property],
    property,
    required: false,
  };
}

function defaultLabelFor(field: CustomFieldDef): string {
  const kind = questionKindForFieldType(field.type);
  return kind === "attachment" ? "Photos" : kind ? FORM_QUESTION_LABEL[kind] : "";
}

function authoredLabel(item: FormFieldItem, previousField: CustomFieldDef | null): string | null {
  const inherited =
    item.kind === "question" || item.kind === "attachment"
      ? [item.field.name, defaultLabelFor(item.field)]
      : [previousField?.name, previousField ? defaultLabelFor(previousField) : null];
  return item.label.trim() !== "" && !inherited.includes(item.label) ? item.label : null;
}

export function linkItemToField(
  item: FormFieldItem,
  field: CustomFieldDef,
  previousField: CustomFieldDef | null,
): FormItem {
  return {
    customFieldId: field.customFieldId,
    description: item.description ?? null,
    fieldType: field.type,
    itemId: item.itemId,
    kind: "customField",
    label: authoredLabel(item, previousField) ?? field.name,
    required: item.required,
    // The form condition survives a link: where the answer goes is not why it is asked.
    ...(item.showWhen ? { showWhen: item.showWhen } : {}),
  };
}

export function linkItemToProperty(
  item: FormFieldItem,
  property: FormProperty,
  previousField: CustomFieldDef | null,
): FormItem {
  return {
    description: item.description ?? null,
    itemId: item.itemId,
    kind: "property",
    label: authoredLabel(item, previousField) ?? FORM_PROPERTY_LABEL[property],
    property,
    required: item.required,
    ...(item.showWhen ? { showWhen: item.showWhen } : {}),
  };
}

// A deleted field unlinks into its remembered `fieldType`; Text only for rows stored before it existed.
export function unlinkItem(item: FormLinkedItem, field: CustomFieldDef | null): FormItem {
  const base = {
    description: item.description ?? null,
    itemId: item.itemId,
    label: item.label,
    required: item.required,
    ...(item.showWhen ? { showWhen: item.showWhen } : {}),
  };
  if (!field) {
    const shape =
      item.kind === "property" ? FORM_PROPERTY_FIELD_TYPE[item.property] : item.fieldType;
    const remembered = shape ? questionKindForFieldType(shape) : null;
    const kind = remembered ?? "shortText";
    return kind === "attachment"
      ? {
          ...base,
          field: makeInlineField(kind, item.label, item.itemId),
          kind: "attachment",
        }
      : { ...base, field: makeInlineField(kind, item.label, item.itemId), kind: "question" };
  }
  const inline: CustomFieldDef = {
    __typename: "CustomField",
    config: field.config ? { ...field.config } : emptyConfig(),
    customFieldId: `${INLINE_FIELD_ID_PREFIX}${item.itemId}`,
    description: null,
    id: `CustomField:${INLINE_FIELD_ID_PREFIX}${item.itemId}`,
    key: null,
    name: item.label,
    type: field.type === "dateTime" ? "date" : field.type,
  };
  if (field.type === "dateTime") {
    inline.config = { ...(inline.config ?? emptyConfig()), includeTime: true };
  }
  const defaults = linkedDefaultAnswer(item, field);
  if (defaults) {
    inline.config = {
      ...(inline.config ?? emptyConfig()),
      defaultValue: (field.type === "select" ? defaults[0] : defaults) as NonNullable<
        CustomFieldDef["config"]
      >["defaultValue"],
    };
  }
  return field.type === "photos"
    ? { ...base, field: inline, kind: "attachment" }
    : { ...base, field: inline, kind: "question" };
}

export function formItemNotice(
  item: FormItem,
  customFieldsById: ReadonlyMap<string, CustomFieldDef>,
): { kind: "hidden" | "relaxed"; reason: string } | null {
  if (!isFieldItem(item)) return null;
  const field = resolveItemField(item, customFieldsById);
  if (!field) return { kind: "hidden", reason: "Its linked field was deleted" };
  if (!isFillableItem(item, customFieldsById)) return { kind: "hidden", reason: "No choices to pick" };
  if (field.type === "labels" && !isChoiceCountMet(withItemRules(item, field).config)) {
    return {
      kind: "relaxed",
      reason:
        (field.config?.min ?? 0) > choiceCount(field)
          ? "Min choices is more than the choices"
          : "Max choices is less than the required choices",
    };
  }
  if ((field.type === "date" || field.type === "dateTime") && isDateWindowClosed(field.config)) {
    return { kind: "relaxed", reason: "Its date window has ended" };
  }
  return null;
}

// An offer naming nothing matches nothing, so null; an empty `in` filters nothing on the server.
export function offeredOnWhere(
  offer: FormOffer | undefined,
): IssueWhereInput | undefined | null {
  if (!offer) return undefined;
  const branches: IssueWhereInput[] = [
    ...(offer.workflowIds.length > 0 ? [{ workflowId: { in: offer.workflowIds } }] : []),
    ...(offer.issueTypeIds.length > 0 ? [{ issueTypeId: { in: offer.issueTypeIds } }] : []),
  ];
  return branches.length > 0 ? { OR: branches } : null;
}

// The page before the first section has no heading, so a section never swaps with it.
export function withSectionMoved(
  items: readonly FormItem[],
  sectionItemId: string,
  by: -1 | 1,
): FormItem[] {
  const steps = formSteps({ items: [...items] });
  const from = steps.findIndex((step) => step.section?.itemId === sectionItemId);
  const to = from + by;
  const moving = steps[from];
  const other = steps[to];
  if (!moving || !other?.section) return [...items];
  steps[from] = other;
  steps[to] = moving;
  const moved = steps.flatMap((step) => step.items);
  return strandsRule(items, moved) ? [...items] : moved;
}

export function canMoveSection(items: readonly FormItem[], sectionItemId: string, by: -1 | 1): boolean {
  const moved = withSectionMoved(items, sectionItemId, by);
  return moved.some((item, index) => item.itemId !== items[index]?.itemId);
}

function strandsRule(before: readonly FormItem[], after: readonly FormItem[]): boolean {
  const seats = (list: readonly FormItem[]) => new Map(list.map((item, index) => [item.itemId, index]));
  const was = seats(before);
  const now = seats(after);
  return after.some((item) => {
    const controllerId = item.kind === "section" ? undefined : item.showWhen?.whenItemId;
    if (controllerId === undefined) return false;
    const wasAbove = (was.get(controllerId) ?? Infinity) < (was.get(item.itemId) ?? -1);
    const nowBelow = (now.get(controllerId) ?? -1) > (now.get(item.itemId) ?? Infinity);
    return wasAbove && nowBelow;
  });
}

type FormOutline = {
  depth: ReadonlyMap<string, number>;
  parentId: ReadonlyMap<string, string | null>;
  runOf(itemId: string): string[];
  through: ReadonlyMap<string, readonly number[]>;
};

// Structural only: reads `whenItemId`, never the field, so an unreadable rule still outlines.
export function formOutline(items: readonly FormItem[]): FormOutline {
  const depth = new Map<string, number>();
  const parentId = new Map<string, string | null>();
  let open: string[] = [];
  for (const item of items) {
    const controllerId = item.kind === "section" ? undefined : item.showWhen?.whenItemId;
    const at = controllerId === undefined ? -1 : open.indexOf(controllerId);
    if (at === -1) {
      depth.set(item.itemId, 0);
      parentId.set(item.itemId, null);
      open = [item.itemId];
      continue;
    }
    open = open.slice(0, at + 1);
    depth.set(item.itemId, open.length);
    parentId.set(item.itemId, controllerId ?? null);
    open.push(item.itemId);
  }
  const through = new Map<string, readonly number[]>();
  // A shallower item closes every run deeper than itself.
  let openBelow: boolean[] = [];
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index]!;
    const own = depth.get(item.itemId) ?? 0;
    const lanes: number[] = [];
    for (let lane = 1; lane <= own; lane += 1) {
      if (openBelow[lane]) lanes.push(lane);
    }
    through.set(item.itemId, lanes);
    openBelow = openBelow.slice(0, own);
    openBelow[own] = true;
  }
  const runOf = (itemId: string): string[] => {
    const index = items.findIndex((item) => item.itemId === itemId);
    if (index < 0) return [];
    const own = depth.get(itemId) ?? 0;
    const run: string[] = [];
    for (let below = index + 1; below < items.length; below += 1) {
      const next = items[below];
      if (!next || (depth.get(next.itemId) ?? 0) <= own) break;
      run.push(next.itemId);
    }
    return run;
  };
  return { depth, parentId, runOf, through };
}

// Lands after the whole run; landing after the last follow-up would split it from its own follow-ups.
export function followUpInsertIndex(items: readonly FormItem[], itemId: string): number {
  const index = items.findIndex((item) => item.itemId === itemId);
  if (index < 0) return items.length;
  return index + 1 + formOutline(items).runOf(itemId).length;
}

// A section seated inside a run would cut the run in two.
export function rootSeatAt(items: readonly FormItem[], index: number): number {
  const { depth } = formOutline(items);
  let seat = Math.max(0, Math.min(index, items.length));
  while (seat < items.length && (depth.get(items[seat]!.itemId) ?? 0) > 0) seat += 1;
  return seat;
}

const LONG_PAGE_QUESTIONS = 8;

// A seat qualifies only when each side keeps a third of the questions.
export function longPageSplit(
  form: Pick<Form, "items">,
): { seat: number; step: FormStep } | null {
  const { depth } = formOutline(form.items);
  let start = 0;
  for (const step of formSteps(form)) {
    const end = start + step.items.length;
    const total = fieldItemCount(step);
    if (total >= LONG_PAGE_QUESTIONS) {
      let above = 0;
      let best: { seat: number; gap: number } | null = null;
      for (let seat = start + 1; seat < end; seat += 1) {
        if (isFieldItem(form.items[seat - 1]!)) above += 1;
        if ((depth.get(form.items[seat]!.itemId) ?? 0) > 0) continue;
        if (Math.min(above, total - above) * 3 < total) continue;
        const gap = Math.abs(total - 2 * above);
        if (!best || gap < best.gap) best = { seat, gap };
      }
      if (best) return { seat: best.seat, step };
    }
    start = end;
  }
  return null;
}

// A released follow-up moves past its run: a root inside a run would cut it in two.
export function withConditionReleased(items: readonly FormItem[], itemId: string): FormItem[] {
  const from = items.findIndex((item) => item.itemId === itemId);
  const item = items[from];
  if (!item || item.kind === "section" || !item.showWhen) return [...items];
  const { showWhen: _cleared, ...released } = item;
  const outline = formOutline(items);
  const runLength = outline.runOf(itemId).length;
  const block: FormItem[] = [released, ...items.slice(from + 1, from + 1 + runLength)];
  const rest = [...items.slice(0, from), ...items.slice(from + 1 + runLength)];
  let to = from;
  if ((outline.depth.get(itemId) ?? 0) > 0) {
    to = rest.length;
    for (let index = from; index < rest.length; index += 1) {
      if ((outline.depth.get(rest[index]!.itemId) ?? 0) === 0) {
        to = index;
        break;
      }
    }
  }
  rest.splice(to, 0, ...block);
  return rest;
}

export function withRunMoved(
  items: readonly FormItem[],
  itemId: string,
  index: number,
  patch?: (item: FormItem) => FormItem,
): FormItem[] {
  const from = items.findIndex((item) => item.itemId === itemId);
  if (from < 0) return [...items];
  const length = 1 + formOutline(items).runOf(itemId).length;
  if (index > from && index < from + length) return [...items];
  const [head, ...tail] = items.slice(from, from + length);
  const rest = [...items.slice(0, from), ...items.slice(from + length)];
  rest.splice(index > from ? index - length : index, 0, patch ? patch(head!) : head!, ...tail);
  return rest;
}

export function withItemRemoved(items: readonly FormItem[], itemId: string): FormItem[] {
  const from = items.findIndex((item) => item.itemId === itemId);
  if (from < 0) return [...items];
  const outline = formOutline(items);
  const runLength = outline.runOf(itemId).length;
  if ((outline.depth.get(itemId) ?? 0) === 0 || runLength === 0) {
    return items.filter((item) => item.itemId !== itemId);
  }
  const block = items.slice(from + 1, from + 1 + runLength);
  const rest = [...items.slice(0, from), ...items.slice(from + 1 + runLength)];
  let to = rest.length;
  for (let index = from; index < rest.length; index += 1) {
    if ((outline.depth.get(rest[index]!.itemId) ?? 0) === 0) {
      to = index;
      break;
    }
  }
  rest.splice(to, 0, ...block);
  return rest;
}

// A replacement already on the form moves, so it is never listed twice.
export function withItemReplaced(
  items: readonly FormItem[],
  itemId: string,
  next: FormItem,
  keep = false,
): FormItem[] {
  if (!items.some((item) => item.itemId === itemId)) return [...items];
  const run = new Set(
    items.some((item) => item.itemId === next.itemId) ? formOutline(items).runOf(next.itemId) : [],
  );
  if (run.has(itemId)) run.clear();
  const block = [next, ...items.filter((item) => run.has(item.itemId))];
  const list = items.filter((item) => item.itemId !== next.itemId && !run.has(item.itemId));
  const seat = list.findIndex((item) => item.itemId === itemId);
  if (keep) {
    list.splice(seat, 0, ...block);
    return withConditionReleased(list, itemId);
  }
  list.splice(seat, 1, ...block);
  return list;
}

// Old option value maps to the same value in the new set, else the option with the same label.
function carriedOptionValues(
  from: CustomFieldDef | null,
  to: CustomFieldDef | null,
): Map<string, string> {
  const rowsOf = (field: CustomFieldDef | null) =>
    (field?.config?.options ?? []).flatMap((option) => {
      const value = option?.value ?? option?.label ?? "";
      return value === ""
        ? []
        : [{ key: (option?.label ?? value).trim().toLowerCase(), value }];
    });
  const target = rowsOf(to);
  const values = new Set(target.map((row) => row.value));
  const carried = new Map<string, string>();
  for (const row of rowsOf(from)) {
    const match = values.has(row.value)
      ? row.value
      : target.find((candidate) => candidate.key === row.key)?.value;
    if (match !== undefined) carried.set(row.value, match);
  }
  return carried;
}

// A rule left naming no answer stays on its follow-up, asking everyone until the author picks one.
export function withItemRelinked(
  items: readonly FormItem[],
  itemId: string,
  next: FormItem,
  from: CustomFieldDef | null,
  to: CustomFieldDef | null,
): { items: FormItem[]; unanswered: string[] } {
  const current = items.find((item) => item.itemId === itemId);
  if (!current) return { items: [...items], unanswered: [] };
  const carried = carriedOptionValues(from, to);
  const unanswered: string[] = [];
  return {
    items: items.map((item) => {
      if (item.itemId === itemId) return next;
      const rule = item.kind === "section" ? undefined : item.showWhen;
      if (!rule || rule.whenItemId !== itemId || !rule.values || rule.values.length === 0) {
        return item;
      }
      const values = [
        ...new Set(rule.values.flatMap((value) => carried.get(value) ?? [])),
      ];
      if (values.length === 0) unanswered.push(item.itemId);
      return { ...item, showWhen: { ...rule, values } };
    }),
    unanswered,
  };
}

// A follow-up never rises above its controller, and a section never lands inside a run.
export function reorderWithRun(
  items: readonly FormItem[],
  activeId: string,
  overId: string,
): FormItem[] {
  const next = reorderBlock(items, activeId, overId);
  return strandsRule(items, next) ? [...items] : next;
}

function reorderBlock(
  items: readonly FormItem[],
  activeId: string,
  overId: string,
): FormItem[] {
  const from = items.findIndex((item) => item.itemId === activeId);
  const to = items.findIndex((item) => item.itemId === overId);
  const active = items[from];
  if (!active || to === -1 || from === to) return [...items];
  const outline = formOutline(items);
  const run = outline.runOf(activeId);
  const block = items.slice(from, from + 1 + run.length);
  if (block.some((item) => item.itemId === overId)) return [...items];
  const others = items.filter((item) => !block.includes(item));
  const overAt = others.findIndex((item) => item.itemId === overId);
  const next = [...others];
  const seat = from < to ? overAt + 1 + outline.runOf(overId).length : overAt;
  next.splice(active.kind === "section" ? rootSeatAt(others, seat) : seat, 0, ...block);

  const controllerId = active.kind === "section" ? undefined : active.showWhen?.whenItemId;
  if (controllerId === undefined) return next;
  const controllerAt = next.findIndex((item) => item.itemId === controllerId);
  const activeAt = next.findIndex((item) => item.itemId === activeId);
  if (controllerAt === -1 || controllerAt < activeAt) return next;
  const clamped = [...others];
  clamped.splice(others.findIndex((item) => item.itemId === controllerId) + 1, 0, ...block);
  return clamped;
}
